import { type ElementType, lazy, Suspense } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { ArcadeButtonLink, ArcadePageHeader, Button, Logo, PageHeader, spacing, Stack } from '@cash-design-system/react'
// todo: fix this once breakpoints exposed on arcade
// eslint-disable-next-line no-restricted-imports
import { BreakpointSmall } from '@cash-design-system/tokens'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import CheckStatusPageButton from './CheckStatusPageButton/CheckStatusPageButton'
import ErrorFooter from './ErrorFooter/ErrorFooter'

const ErrorIllustration = lazy(() =>
  import(/* webpackChunkName: "ErrorIllustration" */ './ErrorIllustration').then(module => ({
    default: module.ErrorIllustration,
  }))
)

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex: 1;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;

  ${({ theme }) => `
    margin: ${theme.spacing.empty} auto;
    padding: ${theme.spacing.xl};
    min-width: ${theme.pxToRem(375)};
    max-width: ${theme.pxToRem(1660)};
    background: ${theme.arcadeColors.semantic.backgroundApp};
    border-top: 1px solid ${theme.arcadeColors.semantic.borderDivider};
    color: ${theme.arcadeColors.semantic.textSubtle};
  `}
`

const LogoWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
`

const Link = styled.a`
  text-decoration: none;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: ${({ theme: { spacing } }) => `${spacing.xl} ${spacing.empty}`};
  max-width: ${BreakpointSmall};
`

interface Props {
  titleText: string | null
  secondaryText: string | null
  buttonText?: string
  redirectUrl?: string
  /** Heading element for the title. Consumers rendering this view as the whole page should pass
   * 'h1' so the error title heads the document outline; defaults to 'h2' for embedded consumers.
   * (The legacy non-Arcade PageHeader branch already defaults to 'h1' on its own.) */
  titleElement?: ElementType
  useAnimation?: boolean
  useFooter?: boolean
  showLogo?: boolean
  renderContainer?: boolean
  renderStatusPageButton?: boolean
}

export function ErrorView({
  titleText,
  secondaryText,
  buttonText,
  redirectUrl = '/',
  titleElement = 'h2',
  useAnimation = false,
  useFooter = false,
  showLogo = true,
  renderContainer = true,
  renderStatusPageButton = true,
}: Props) {
  const { t } = useTranslation()
  const { darkMode } = useTheme()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const buttonCopy =
    buttonText ??
    t('Back to Cash App Home', {
      context: 'Button text to go back to Cash App Home on the error page',
    })

  const content = isArcadeMigrationEnabled ? (
    <>
      {showLogo && (
        <LogoWrapper>
          <Link href={redirectUrl}>
            <Logo colorVariant="greenMonochrome" withText={true} />
          </Link>
        </LogoWrapper>
      )}

      {titleText && secondaryText ? (
        <ArcadePageHeader title={titleText} titleElement={titleElement} body={secondaryText} />
      ) : (
        <ArcadePageHeader
          title={t('An unknown error occurred.', { context: 'Error page title' })}
          titleElement={titleElement}
          body={t('The Cash App team has been notified.', {
            context: 'Information that the Cash App team has been notified for the error page',
          })}
        />
      )}

      <ButtonContainer>
        <Stack space={spacing.m}>
          <ArcadeButtonLink aria-label={buttonCopy} href={redirectUrl}>
            {buttonCopy}
          </ArcadeButtonLink>
          {renderStatusPageButton && <CheckStatusPageButton />}
        </Stack>
      </ButtonContainer>

      <Suspense fallback={null}>
        <ErrorIllustration useAnimation={useAnimation} />
      </Suspense>

      {useFooter && <ErrorFooter />}
    </>
  ) : (
    <>
      {showLogo && (
        <LogoWrapper>
          <Link href={redirectUrl}>
            <Logo colorVariant={darkMode ? 'white' : 'black'} withText={true} />
          </Link>
        </LogoWrapper>
      )}

      {titleText && secondaryText ? (
        <PageHeader title={titleText} subtext={secondaryText} />
      ) : (
        <PageHeader
          title={t('An unknown error occurred.', { context: 'Error page title' })}
          subtext={t('The Cash App team has been notified.', {
            context: 'Information that the Cash App team has been notified for the error page',
          })}
        />
      )}

      <ButtonContainer>
        <Stack space={spacing.m}>
          <Link href={redirectUrl}>
            <Button>
              {buttonText ??
                t('Back to Cash App Home', {
                  context: 'Button text to go back to Cash App Home on the error page',
                })}
            </Button>
          </Link>
          {renderStatusPageButton && <CheckStatusPageButton />}
        </Stack>
      </ButtonContainer>

      <Suspense fallback={null}>
        <ErrorIllustration useAnimation={useAnimation} />
      </Suspense>

      {useFooter && <ErrorFooter />}
    </>
  )
  return renderContainer ? (
    <Container>
      <ContentContainer>{content}</ContentContainer>
    </Container>
  ) : (
    content
  )
}
